import React, { useEffect, useRef, useMemo } from 'react';
import { Button, Flex, Tooltip, Typography } from 'antd';
import * as styles from '@styles/costcoMembershipDashboard.module.scss';
import GenericSearch from '../atoms/GenericSearch';
import { useState } from 'react';
import SearchIcon from '@src/assets/SearchIcon';
import {
  DownloadOutlined,
  RedoOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import ReloadIcon from '@src/assets/ReloadIcon';
import CustomTable from '../templates/CustomTables';
import { VERIFICATION_TYPES } from '@src/constants/verificationTypes';
import { Select } from 'antd';
import podsMobile from '@assets/PODS_mobile.png';
import CostcoDashboardDownloadModal from '../molecules/CostcoDashboardDownloadModal';
import makeApiRequest from '@src/apiCalls/makeApiRequest';
import { API_ENDPOINTS } from '@src/constants/apiEndpoints';
import apiRequestConfig from '@src/interfaces/apiEndpoints.interface';
import { useNotification } from '@src/providers/NotificationProviders';
import { useNavigate } from 'react-router-dom';
import { DataType } from '@src/interfaces/costcoDashboard.interface';
import { debounce } from 'lodash';
import CustomDatePicker from '../atoms/CalanderComponent';
import dayjs from 'dayjs';
import { TotalRequestIcon } from '@src/assets/TotalRequestIcon';
import { TodaysRequestIcon } from '@src/assets/TodaysRequestIcon';
import { WeekRequestIcon } from '@src/assets/WeekRequestIcon';
import CostcoPreferenceDrawer from '../molecules/CostoPreferenceDrawer';
import MembershipDetailsDrawer from '../molecules/MembershipDetailsDrawer';

const { Title, Text } = Typography;

const CostcoMembershipDashboard = () => {
  const openNotification = useNotification();
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState('');
  const [totalElements, setTotalElements] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [dataSource, setDatasource] = useState<DataType[]>([]);
  const hasShown403Notification = useRef(false);
  const [startDate, setStartDate] = useState(
    Math.floor(dayjs().subtract(7, 'days').startOf('day').valueOf() / 1000).toString()
  );
  const [endDate, setEndDate] = useState(
    Math.floor(dayjs().endOf('day').valueOf() / 1000).toString()
  );
  const [totalRequest, setTotalRequest] = useState(0);
  const [todaysRequest, setTodaysRequest] = useState(0);
  const [weeklyRequest, setWeeklyRequest] = useState(0);
  const [open, setOpen] = useState(false);
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<DataType | null>(null);
  const token = localStorage.getItem('costcoAdminToken');
  const [selectedVerifyType, setSelectedVerifyType] = useState('COSTCO');
  const typeSelectRef = useRef<any>(null);
  const [reset, setReset] = useState(false);
  const [dynamicFilters, setDynamicFilters] = useState<any[]>([]);
  const [selectedFilters, setSelectedFilters] = useState<Record<string, string>>({});

  const handleDownload = () => {
    setVisible(true);
  };

  const handleRefresh = () => {
    const newEndDate = dayjs().unix().toString();
    const newStartDate = dayjs().subtract(7, 'days').unix().toString();

    setStartDate(newStartDate);
    setEndDate(newEndDate);
    setCurrentPage(1);
    setReset(true);
    fetchAnalytics();
  };

  const handlePageChange = (page: number, newPageSize: number) => {
    setCurrentPage(page);
    setPageSize(newPageSize);
  };

  const handleDateChange = (
    dates: { startDate: string; endDate: string } | null
  ) => {
    if (dates?.startDate && dates?.endDate) {
      const startTimestamp = Math.floor(
        dayjs(dates.startDate).valueOf() / 1000
      );
      const endTimestamp = Math.floor(dayjs(dates.endDate).valueOf() / 1000);
      setStartDate(startTimestamp.toString());
      setEndDate(endTimestamp.toString());
    } else {
      setStartDate('');
      setEndDate('');
    }
  };

  const handleClear = () => {
    const newEndDate = dayjs().unix().toString();
    const newStartDate = dayjs().subtract(7, 'days').unix().toString();
    setStartDate(newStartDate);
    setEndDate(newEndDate);
    setSearchText('');
    setSelectedFilters({});
    setReset(true);
    fetchAnalytics();
  };

  const handlePreferenceDrawer = () => {
    setOpen(true);
  };

  const handleRowClick = (record: DataType) => {
    return {
      onClick: () => {
        setSelectedRecord(record);
        setDetailDrawerOpen(true);
      },
    };
  };

  const currentTypeConfig = VERIFICATION_TYPES[selectedVerifyType];

  const fetchData = async (page: number, pageSize: number) => {
    setLoading(true);

    const hasSearch = searchText.trim().length > 0;
    const apiConfig = hasSearch
      ? API_ENDPOINTS.GET_COSTCO_DASHBOARD_REQUESTS_SEARCH
      : API_ENDPOINTS.GET_COSTCO_DASHBOARD_REQUESTS;

    let queryParams = hasSearch
      ? `?key=${encodeURIComponent(searchText)}&page=${page}&size=${pageSize}&verification_type=${selectedVerifyType}`
      : `?page=${page}&size=${pageSize}&startTime=${startDate}&endTime=${endDate}&verification_type=${selectedVerifyType}`;

    // Add selected filters to query params
    Object.entries(selectedFilters).forEach(([key, value]) => {
      if (value) {
        queryParams += `&${key}=${encodeURIComponent(value)}`;
      }
    });

    return makeApiRequest({}, apiConfig as apiRequestConfig, queryParams, {
      Authorization: `Bearer ${token}`,
    })
      .then((data) => {
        setLoading(false);
        // Extract dynamic filters from response
        if (data?.data?.filters) {
          setDynamicFilters(data.data.filters);
        }
        setDatasource(
          data?.data?.data?.map((item: any, index: number) => ({
            key: index,
            customerId: item.customerId || 'NA',
            customerEmail: item.customerEmail || 'NA',
            membershipId: item.membershipId || item.membershipNumber || 'NA',
            orderId: item.orderId || 'NA',
            customerPhone: item.customerPhone || 'NA',
            status: item.status || 'NA',
            verificationLink: item.verificationLink || 'NA',
            providerResponse: item.providerResponse || 'NA',
            notificationSent: item.notificationSent,
            updatedAt: item.updatedAt,
            completedAt: item.completedAt,
            createdAt: item.createdAt || 'NA',
            source: item.source?.replace('_', ' ') || 'NA',
            requestBy: item.requestBy || 'NA',
            verificationId: item.verificationId || item.id || 'NA',
            idpVerifiedEmail: item.idpVerifiedEmail || 'NA',
          }))
        );
        setTotalElements(data.data.totalElements);
        setTotalPages(data.data.pageSize);
        setCurrentPage(page);
      })
      .catch((error) => {
        setLoading(false);
        if (error?.status === 403 && !hasShown403Notification.current) {
          hasShown403Notification.current = true;
          openNotification(
            'Your session has been expired, Please login again',
            'Error'
          );
        } else if (error?.status !== 403 && !hasShown403Notification.current) {
          hasShown403Notification.current = true;
          openNotification(error?.message, 'Error');
        }
      });
  };

  const fetchAnalytics = async () => {
    const currentTimestamp = dayjs().unix();
    const query = `?currentTimestamp=${currentTimestamp}&verification_type=${selectedVerifyType}`;

    return makeApiRequest(
      {},
      API_ENDPOINTS.GET_COSTCO_ANALYTICS as apiRequestConfig,
      query,
      {
        Authorization: `Bearer ${token}`,
      }
    )
      .then((res) => {
        const data = res?.data || {};
        setTotalRequest(data.totalRequests || 0);
        setTodaysRequest(data.totalRequestsToday || 0);
        setWeeklyRequest(data.totalRequestsThisWeek || 0);
      })
      .catch((err) => {
        console.error('Failed to fetch analytics', err);
      });
  };

  useEffect(() => {
    fetchAnalytics();
  }, [selectedVerifyType, startDate, endDate]);

  useEffect(() => {
    const delayedFetch = debounce(() => {
      fetchData(currentPage, pageSize);
    }, 300);

    delayedFetch();
    return () => {
      delayedFetch.cancel();
    };
  }, [searchText, startDate, endDate, currentPage, pageSize, selectedVerifyType, selectedFilters]);

  const columnsWithFilters = useMemo(() => {
    if (!currentTypeConfig.columns) return [];

    return currentTypeConfig.columns.map((col: any) => {
      const dynamicFilter = dynamicFilters.find(f => f.column === col.dataIndex);

      if (dynamicFilter) {
        return {
          ...col,
          filters: dynamicFilter.values.map((v: string) => ({ text: v, value: v })),
          filterSearch: true,
          filteredValue: selectedFilters[col.dataIndex]
            ? selectedFilters[col.dataIndex].split(',')
            : null,
          onFilter: () => true,
        };
      }
      return col;
    });
  }, [currentTypeConfig, dynamicFilters, selectedFilters]);

  const handleTableChange = (pagination: any, filters: any, sorter: any, extra: any) => {
    const newFilters: Record<string, string> = {};
    Object.keys(filters).forEach(key => {
      if (filters[key]) {
        newFilters[key] = filters[key].join(',');
      }
    });

    setSelectedFilters(newFilters);
    if (extra?.action !== 'paginate') {
      setCurrentPage(1);
    }
  };

  return (
    <Flex vertical>
      <section className={(styles as any).logo}>
        <img src={podsMobile as any} alt="PODS Logo" />
        <Flex align="center" gap={10} style={{ paddingRight: '20px' }}>
          <Text className={(styles as any).headerLabel}>Verification Type</Text>
          <Select
            ref={typeSelectRef}
            value={selectedVerifyType}
            onChange={(value) => {
              setSelectedVerifyType(value);
              setCurrentPage(1);
              typeSelectRef.current?.blur();
            }}
            showSearch
            optionFilterProp="label"
            style={{ width: 180 }}
            options={Object.values(VERIFICATION_TYPES).map((t) => ({
              label: t.label,
              value: t.key,
            }))}
          />
        </Flex>
      </section>
      <Flex vertical className={(styles as any).mainLayout}>
        <Flex align="center" justify="space-between" style={{ marginBottom: '25px' }}>
          <Title level={2} className={(styles as any).mainTitle} style={{ margin: 0 }}>
            {currentTypeConfig.label} Verifications
          </Title>
          {currentTypeConfig.hasSystemPreferences && (
            <Button icon={<SettingOutlined />} onClick={handlePreferenceDrawer}>
              System Preferences
            </Button>
          )}
        </Flex>
        <Flex className={(styles as any).cardsLayout}>
          <Flex gap={10} className={(styles as any).cards}>
            <svg className={(styles as any).svg}>
              <TotalRequestIcon />
            </svg>
            <Flex vertical>
              <Text className={(styles as any).cardTitle}>Total Requests</Text>
              <Text className={(styles as any).cardValue}>{totalRequest}</Text>
            </Flex>
          </Flex>
          <Flex gap={10} className={(styles as any).cards}>
            <svg className={(styles as any).svg}>
              <TodaysRequestIcon />
            </svg>
            <Flex vertical>
              <Text className={(styles as any).cardTitle}>Todays Requests</Text>
              <Text className={(styles as any).cardValue}>{todaysRequest}</Text>
            </Flex>
          </Flex>
          <Flex gap={10} className={(styles as any).cards}>
            <svg className={(styles as any).svg}>
              <WeekRequestIcon />
            </svg>
            <Flex vertical>
              <Text className={(styles as any).cardTitle}>This Week Requests</Text>
              <Text className={(styles as any).cardValue}>{weeklyRequest}</Text>
            </Flex>
          </Flex>
        </Flex>
        <Flex vertical>
          <Flex className={(styles as any).tableHeader}>
            <GenericSearch
              placeholder="Search"
              onChange={(e) => setSearchText(e.target.value)}
              prefix={<SearchIcon />}
              style={{ width: '24%' }}
              allowClear
            />
            <Flex gap={15} style={{ alignItems: 'center' }}>
              <Tooltip title="Export Requests">
                <span
                  className={(styles as any).downloadLayout}
                  onClick={handleDownload}
                >
                  <Typography className={(styles as any).exportLabel}>Export</Typography>
                  <DownloadOutlined />
                </span>
              </Tooltip>
              <span className={(styles as any).refreshButton} onClick={handleRefresh}>
                <Typography className={(styles as any).refreshLabel}>Refresh</Typography>
                <ReloadIcon />
              </span>
              <Flex gap={10}>
                <CustomDatePicker
                  onDateRangeChange={handleDateChange}
                  reset={reset}
                  setReset={setReset}
                />
                <Tooltip title="Reset Filter">
                  <Button
                    className={(styles as any).filterResetButton}
                    type="text"
                    icon={<RedoOutlined />}
                    onClick={handleClear}
                  />
                </Tooltip>
              </Flex>
            </Flex>
          </Flex>
          <CustomTable
            dataSource={dataSource}
            columns={columnsWithFilters}
            totalElements={totalElements}
            totalPages={totalPages}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            onChange={handleTableChange}
            includePagination
            loading={loading}
            scrollY={550}
            onRow={handleRowClick}
          />
        </Flex>
      </Flex>
      <CostcoDashboardDownloadModal
        title={`Export ${currentTypeConfig.label} Members`}
        visible={visible}
        setVisible={setVisible}
        verificationType={selectedVerifyType}
      />
      <CostcoPreferenceDrawer open={open} setOpen={setOpen} />
      <MembershipDetailsDrawer
        open={detailDrawerOpen}
        onClose={() => setDetailDrawerOpen(false)}
        data={selectedRecord}
        verificationType={selectedVerifyType}
        onRefresh={() => fetchData(currentPage, pageSize)}
      />
    </Flex>
  );
};
export default CostcoMembershipDashboard;
